<template>
  <div class="shop-container">
    <main class="bd-grid">
      <div
        v-for="product in props.modelValue"
        :key="product.id"
        class="product-item"
        @click="goToProduct(product.id)"
      >
        <div class="card">
          <div class="card-img">
            <img
              :src="getImage(product.image)"
              :alt="product.name"
              loading="lazy"
              decoding="async"
              width="300"
              height="300"
            />
          </div>

          <div class="card-content">
            <p class="card-name">{{ product.name }}</p>

            <!-- Rating Section -->
            <div class="product-rating" v-if="product.ratingActive == 1">
              <div class="stars">
                <span
                  v-for="n in 5"
                  :key="n"
                  class="star"
                  :class="{
                    filled:
                      n <=
                      Math.round(product.rating != null ? product.rating : 5),
                  }"
                >
                  <!-- SVG Star for better quality -->
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    :fill="
                      n <=
                      Math.round(product.rating != null ? product.rating : 5)
                        ? 'url(#goldGradient)'
                        : 'currentColor'
                    "
                    class="star-icon"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M10.788 3.21c.448-1.077 1.976-1.077 2.424 0l2.082 5.007 5.404.433c1.164.093 1.636 1.545.749 2.305l-4.117 3.527 1.257 5.273c.271 1.136-.964 2.033-1.96 1.425L12 18.354 7.373 21.18c-.996.608-2.231-.29-1.96-1.425l1.257-5.273-4.117-3.527c-.887-.76-.415-2.212.749-2.305l5.404-.433 2.082-5.006z"
                      clip-rule="evenodd"
                    />
                  </svg>
                </span>
              </div>
              <span class="rating-value">{{
                Number(product.rating != null ? product.rating : 5).toFixed(1)
              }}</span>
            </div>

            <div class="card-prices">
              <div v-if="getPromoPrice(product) != 0" class="price-container">
                <span class="new-price">{{ getPromoPrice(product) }} DA</span>
                <span class="old-price">{{ getBeforePrice(product) }} DA</span>
              </div>
              <div v-else class="price-container">
                <span class="regular-price"
                  >{{ getBeforePrice(product) }} DA</span
                >
              </div>
            </div>
          </div>

          <div class="product-variants" v-if="computedVariants[product.id]">
            <div
              v-if="
                computedVariants[product.id].models &&
                computedVariants[product.id].models.length > 1
              "
              class="variant-models"
            >
              <img
                v-for="(model, index) in computedVariants[
                  product.id
                ].models.slice(0, 4)"
                :key="'m-' + index"
                :src="getImage(model.image)"
                class="model-dot"
                :title="model.modelName"
              />
              <span
                v-if="computedVariants[product.id].models.length > 4"
                class="more-variants"
                >+{{ computedVariants[product.id].models.length - 4 }}</span
              >
            </div>
            <template v-else>
              <div
                class="variant-colors"
                v-if="computedVariants[product.id].colors.length > 0"
              >
                <span
                  v-for="(color, index) in computedVariants[
                    product.id
                  ].colors.slice(0, 4)"
                  :key="'c-' + index"
                  class="color-dot"
                  :style="{ backgroundColor: color.color }"
                  :title="color.color_name"
                ></span>
                <span
                  v-if="computedVariants[product.id].colors.length > 4"
                  class="more-variants"
                  >+{{ computedVariants[product.id].colors.length - 4 }}</span
                >
              </div>
              <div
                class="variant-sizes"
                v-if="computedVariants[product.id].sizes.length > 0"
              >
                <span
                  v-for="(size, index) in computedVariants[
                    product.id
                  ].sizes.slice(0, 3)"
                  :key="'s-' + index"
                  class="size-tag"
                  >{{ size.size }}</span
                >
                <span
                  v-if="computedVariants[product.id].sizes.length > 3"
                  class="more-variants"
                  >+{{ computedVariants[product.id].sizes.length - 3 }}</span
                >
              </div>
            </template>
          </div>

          <button class="buyBtn magical-button">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="btn-icon"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"
              />
            </svg>
            {{ $t("buyNow") || "Acheter" }}
          </button>
        </div>
      </div>
    </main>
  </div>
</template>

<script setup>
import { useRouter } from "vue-router";
import { defineProps, computed } from "vue";

const props = defineProps({
  modelValue: {
    type: Array,
    required: true,
  },
});

//console.log('props: ', props.modelValue)

const router = useRouter();

const getImage = (image) => {
  if (!image) return "/images/placeholder.png";
  return image.startsWith("https")
    ? image
    : `https://gestionshoop.shooplinkdz.com${image}`;
};

const formatPrice = (value) => {
  if (value === "" || value === null || value === undefined) return "";
  return Number(value) % 1 === 0 ? parseInt(value) : value;
};

const getBeforePrice = (product) => {
  const price =
    product.models?.[0]?.original || product.models?.[0]?.sell || "";
  return formatPrice(price);
};

const getPromoPrice = (product) => {
  const promo = product.models?.[0]?.promo || "";
  return formatPrice(promo);
};

const goToProduct = (id) => {
  router.push(`/produits/${id}`);
};

const computedVariants = computed(() => {
  const map = {};
  for (let product of props.modelValue) {
    const colors = [];
    const sizes = [];
    const models = [];
    if (product.models) {
      for (let model of product.models) {
        if (model.isActive == 1 || model.isActive === "1") {
          models.push({
            image: model.imageUrls || model.image,
            modelName: model.name || model.modelName,
          });
        }
        if (model.activeColor == 1 && model.details) {
          for (let d of model.details) {
            if (d.color && !colors.find((c) => c.color === d.color)) {
              colors.push({ color: d.color, color_name: d.colorName });
            }
          }
        }
        if (model.activeSize == 1 && model.details) {
          for (let d of model.details) {
            if (d.size && !sizes.find((s) => s.size === d.size)) {
              sizes.push({ size: d.size });
            }
          }
        }
      }
    }
    if (colors.length > 0 || sizes.length > 0 || models.length > 1) {
      map[product.id] = { colors, sizes, models };
    }
  }
  return map;
});
</script>

<style scoped>
.shop-container {
  padding: 0 20px;
  max-width: 1200px;
  margin: 0 auto;
}

.bd-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 10px;
  margin: 10px 0;
}

@media (max-width: 300px) {
  .bd-grid {
    grid-template-columns: repeat(1, 1fr);
    gap: 12px;
  }
  .shop-container {
    padding: 0 10px;
  }
}

.card {
  background: white;
  border-radius: var(--radius);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  cursor: pointer;
  transition: transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease;
  height: 100%;
  border: 1px solid var(--border, #e5e7eb);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.02);
}

.card:hover {
  transform: translateY(-4px);
  border-color: var(--secondary);
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.05);
}

.card-img {
  width: 100%;
  aspect-ratio: 1 / 1;
  background: #f8fafc;
  overflow: hidden;
  position: relative;
}

.card-img img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;
}

.card:hover .card-img img {
  transform: scale(1.05);
}

.card-content {
  padding: 12px;
  text-align: left;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
}

.card-name {
  font-weight: 600;
  color: var(--dark);
  font-size: 0.95rem;
  margin: 0 0 6px 0;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.price-container {
  display: flex;
  align-items: baseline;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: auto; /* Push price to bottom of content area */
}

.new-price,
.regular-price {
  color: var(--dark);
  font-weight: 700;
  font-size: 1rem;
}

.old-price {
  text-decoration: line-through;
  color: #94a3b8;
  font-size: 0.85rem;
}

.buyBtn {
  width: 100%;
  padding: 14px;
  background: linear-gradient(135deg, var(--secondary) 0%, var(--accent) 100%);
  color: white;
  border: none;
  font-size: 0.95rem;
  font-weight: 700;
  cursor: pointer;
  margin-top: auto;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-radius: 0 0 var(--radius) var(--radius); /* Hug the bottom corners */
}

.btn-icon {
  width: 18px;
  height: 18px;
}

.buyBtn:hover {
  background: var(--accent);
}

.product-variants {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-block: 5px;
  margin-inline: 2px;
  align-items: center;
}

.variant-colors {
  display: flex;
  gap: 4px;
  align-items: center;
}

.color-dot {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.variant-models {
  display: flex;
  gap: 4px;
  align-items: center;
}

.model-dot {
  width: 50px;
  height: 50px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  object-fit: cover;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.variant-sizes {
  display: flex;
  gap: 4px;
  align-items: center;
}

.size-tag {
  font-size: 0.85rem;
  padding: 4px 8px;
  background: #f1f5f9;
  color: var(--dark);
  border-radius: 4px;
  font-weight: 600;
}

.more-variants {
  font-size: 0.7rem;
  color: var(--gray);
  font-weight: 600;
  margin-left: 2px;
}

/* Rating Styles */
.product-rating {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
}

.stars {
  display: flex;
  gap: 1px;
}

.star {
  color: #e2e8f0;
  display: flex;
  align-items: center;
}

.star.filled {
  color: var(--secondary); /* Magical Gold */
}

.star-icon {
  width: 14px;
  height: 14px;
}

.rating-value {
  color: #64748b;
  font-size: 0.8rem;
  font-weight: 500;
  padding-top: 2px;
}
</style>
